import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, KeyRound } from 'lucide-react';
import type { KitCode } from '@mlm/shared';
import { kits } from '../lib/api';
import { qk } from '../lib/queries';
import { instantDay } from '../lib/format';
import { CoachButton, Eyebrow, ErrorNote, Loading, Screen } from '../components/primitives';
import { ShopNav } from '../components/shop-nav';

/**
 * The member's Code+PIN vault. Codes are stored and shown in PLAINTEXT by
 * design (gift-card UX: staff read a PIN back over the phone), so this screen is
 * displaying live credentials and says so rather than pretending otherwise.
 */
export function VaultPage() {
  const q = useQuery({ queryKey: qk.myCodes, queryFn: () => kits.myCodes(), staleTime: 30_000 });
  const codes = q.data ?? [];
  const available = codes.filter((c) => !c.redeemedAt);
  const used = codes.filter((c) => c.redeemedAt);

  return (
    <Screen eyebrow="Shop" title="Your codes" wide>
      <ShopNav />

      <div className="border-b border-border px-5 py-5">
        <Link to="/vault/redeem" className="block">
          <CoachButton kind="gold" block>
            <KeyRound className="h-4 w-4" aria-hidden />
            Activate a code
          </CoachButton>
        </Link>
      </div>

      {q.isPending ? (
        <Loading label="Loading your codes" />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : codes.length === 0 ? (
        <div className="px-5 py-14 text-center">
          <Eyebrow>Nothing here</Eyebrow>
          <p className="mx-auto mt-2 max-w-sm text-[0.93rem] leading-relaxed text-ink/70">
            Codes appear here after you buy a kit. Each one activates a membership slot.
          </p>
        </div>
      ) : (
        <>
          {available.length > 0 && (
            <>
              <SectionLabel>Ready to use</SectionLabel>
              <ul>
                {available.map((c) => (
                  <CodeRow key={c.id} code={c} />
                ))}
              </ul>
            </>
          )}
          {used.length > 0 && (
            <>
              <SectionLabel>Already used</SectionLabel>
              <ul>
                {used.map((c) => (
                  <CodeRow key={c.id} code={c} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </Screen>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="border-b border-border bg-surface px-5 py-2 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-sage-deep">
      {children}
    </p>
  );
}

function CodeRow({ code }: { code: KitCode }) {
  const done = Boolean(code.redeemedAt);
  const expired =
    !done && code.expiresAt != null && new Date(code.expiresAt).getTime() < Date.now();
  return (
    <li className="border-b border-border px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[0.93rem] leading-snug text-ink">{code.productName}</p>
          <p className="mt-1 font-mono text-[0.85rem] tracking-wider text-ink">
            {code.code} <span className="text-sage-deep">/</span> {code.pin}
          </p>
          <p className="mt-1 text-[0.85rem] text-sage-deep">
            {done
              ? `Used ${instantDay(code.redeemedAt!)}`
              : expired
                ? `Expired ${instantDay(code.expiresAt!)}`
                : code.expiresAt
                  ? `Use by ${instantDay(code.expiresAt)}`
                  : 'No expiry'}
            {code.usageMode === 'upgrade' ? ' · upgrade only' : ''}
            {code.slotType && code.slotType !== 'paid' ? ` · ${code.slotType}` : ''}
          </p>
        </div>
        {done ? (
          <Check className="mt-1 h-4 w-4 shrink-0 text-verde" aria-hidden />
        ) : !expired ? (
          <Link
            to={`/vault/redeem?code=${encodeURIComponent(code.code)}&pin=${encodeURIComponent(code.pin)}`}
            className="shrink-0"
          >
            <CoachButton>Use</CoachButton>
          </Link>
        ) : null}
      </div>
    </li>
  );
}
